import {NotifyPropertyChanged} from '../utils/NotifyPropertyChanged.js';
import {gameState} from './GameState.js';
import {Player} from './Player.js';
import {Weapon} from './combat/Weapon.js';
import {Constants} from './data/constants.js';
import {addEnemyAudio} from '../utils/Audio.js';

import type {GameAnalytics as GA} from 'gameanalytics';
declare var GameAnalytics: typeof GA;

export enum EnemyStates {
    Init, // init state is doing nothing
    Spawn, // spawn state is the enemy spawning, after which it goes to idle
    Idle,
    Move,
    Hit,
    Attack,
    Dead,
    Aggro,
}

type CanHitPlayerCallback = () => boolean;

export class Enemy extends NotifyPropertyChanged {
    name: string;
    level: number;
    weapon: Weapon | null;

    /**
     * The health of the enemy, calculated from the level
     */
    health: number;

    // callback function to check if we can hit a player
    canHitPlayer: CanHitPlayerCallback | null = null;

    private _state: EnemyStates = EnemyStates.Init;
    get state() {
        return this._state;
    }

    get dead() {
        return this.health <= 0;
    }

    /**
     * Is the enemy currently targeting a player?
     * null if not agro, otherwise the player being targeted
     */
    private _isAgroTargeting: Player | null = null;

    constructor(name: string, level: number, weapon: Weapon | null = null) {
        super();
        this.name = name;
        this.level = level;
        this.weapon = weapon;
        this.health = level * Constants.enemyHealthMP;
        addEnemyAudio(this);
    }

    clearAggro() {
        this._isAgroTargeting = null;
    }

    aggro(player: Player) {
        if (this.state === EnemyStates.Dead) {
            // don't agro if enemy is dead
            return;
        }
        this._isAgroTargeting = player;
        this.setState(EnemyStates.Aggro);
        this.attack();
    }

    /**
     *
     * @param damage damage to take
     * @param player player dealing the damage
     * @returns true if the enemy or player is dead
     */
    takeDamage(damage: number, player: Player): boolean {
        if (this.state === EnemyStates.Dead) {
            // don't take damage if enemy is dead
            return true;
        }

        let newAttack = false;
        // if the enemy is not agroed, target the player
        if (!this._isAgroTargeting) {
            this._isAgroTargeting = player;
            this.attack();
            newAttack = true;
        }

        // if the player is not the current target, switch targets
        if (this._isAgroTargeting !== player) {
            this._isAgroTargeting = player;
        }

        // take the damage
        this.health -= damage;

        gameState.cameraShake();

        // if the enemy is dead, remove the target
        if (this.health <= 0) {
            // stop any attack
            clearTimeout(this._attackInterval);
            this.setState(EnemyStates.Dead);
            this._isAgroTargeting = null;
            return true;
        }
        this.setState(newAttack ? EnemyStates.Attack : EnemyStates.Hit);

        return false;
    }

    /**
     * value to keep track of the attack interval timer
     */
    private _attackInterval: number = 0;

    attack() {
        if (!this._isAgroTargeting || this.state !== EnemyStates.Attack) {
            console.log(`${this.name} is not targeting anyone`);
            clearTimeout(this._attackInterval);
            this._attackInterval = 0;
            return;
        }
        // actually hit the player halfway through the attack animation
        setTimeout(() => {
            if (
                this.canHitPlayer &&
                this.canHitPlayer() &&
                this._isAgroTargeting &&
                this.state === EnemyStates.Attack
            ) {
                let damage = Math.ceil(this.level * Constants.enemyDamageMP);
                if (this.weapon) {
                    damage += this.weapon.baseDamage;
                }
                const effectiveDamage = this._isAgroTargeting.takeDamage(damage);
                if (effectiveDamage && this._isAgroTargeting.dead) {
                    GameAnalytics.addDesignEvent('player:die:' + this.name);
                }
            }
        }, Constants.hitDelay);

        this._attackInterval = setTimeout(() => {
            if (this._isAgroTargeting && this.state !== EnemyStates.Move) {
                // schedule the next attack
                this.attack();
            }
        }, Constants.enemyAttackI);
    }

    setState(newState: EnemyStates) {
        if (newState === this._state) {
            return; // don't do anything if the state is the same
        }

        this._state = newState;
        this.notifyPropertyChanged('state');

        if (newState === EnemyStates.Spawn) {
            setTimeout(() => {
                this.setState(EnemyStates.Idle);
            }, 1200);
        }
        if (
            (newState === EnemyStates.Idle || newState === EnemyStates.Hit) &&
            this._isAgroTargeting
        ) {
            this.setState(EnemyStates.Attack);
        }

        if (newState === EnemyStates.Attack) {
            this.attack();
        }

        if (newState === EnemyStates.Dead) {
            this._isAgroTargeting = null;
            GameAnalytics.addDesignEvent(`enemy:${this.name}:kill`);
            setTimeout(() => {
                this.notifyPropertyChanged('dead');
            }, 2000);
        }
    }
}
